import { useState } from 'react'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { toast } from 'sonner'

import { Banner } from '@/components/ui/banner'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { SkeletonRows } from '@/components/ui/skeleton'
import { ClaimStatus, submitClaimRequest, useClaim } from '../../../../models/claim'
import { useCurrentUser, useOrganisation } from '../../../../models/user'
import { ClaimReviewSummary } from './ClaimReviewSummary'
import { ClaimStepShell } from './ClaimStepShell'
import { formatFullDate } from './claimSummary'

/** Where the claim still waits on the broker, so the card may promise their review. */
const WITH_BROKER: ClaimStatus[] = [ClaimStatus.SUBMITTED, ClaimStatus.IN_REVIEW, ClaimStatus.INFO_NEEDED]

interface CertForm {
  certifyTrue: boolean
  authorizeInvestigate: boolean
  signature: string
}

interface ClaimReviewFormProps {
  claimId: string
  /** On-behalf org scope (CAI user acting for a customer). */
  orgId?: string
  onBack: () => void
  /** The step header and the Supplemental banner; off beside the lifecycle timeline. */
  showHeader?: boolean
}

export function ClaimReviewForm({ claimId, orgId, onBack, showHeader = true }: ClaimReviewFormProps) {
  // Fetch the current claim so the summary reflects everything saved across steps 1–4.
  const { data: claim, isLoading, refetch } = useClaim(claimId, orgId)
  const { user } = useCurrentUser({ enabled: true })
  const [saving, setSaving] = useState(false)
  const [justSubmitted, setJustSubmitted] = useState(false)

  // Org context for the policy id + insured name.
  const ownOrg = user?.currentOrg
  // No org_id means a customer reading their own claim.
  const isOwnOrg = !!ownOrg && (!orgId || orgId === ownOrg.id)
  const { organisation } = useOrganisation(user && !isOwnOrg ? orgId : undefined)
  const org = isOwnOrg ? ownOrg : organisation

  const { control, register, handleSubmit } = useForm<CertForm>({
    defaultValues: { certifyTrue: false, authorizeInvestigate: false, signature: '' },
  })
  const certifyTrue = useWatch({ control, name: 'certifyTrue' })
  const authorizeInvestigate = useWatch({ control, name: 'authorizeInvestigate' })
  const signature = useWatch({ control, name: 'signature' })
  const canSubmit = certifyTrue && authorizeInvestigate && signature.trim().length > 0

  async function onValid(v: CertForm) {
    window.scrollTo({ top: 0, behavior: 'smooth' })
    setSaving(true)
    try {
      await submitClaimRequest(
        claimId,
        {
          signature: v.signature,
          certifyTrue: v.certifyTrue,
          authorizeInvestigate: v.authorizeInvestigate,
        },
        orgId
      )
      setJustSubmitted(true)
      void refetch()
      toast.success('Claim submitted.')
    } catch {
      toast.error('Could not submit. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  if (isLoading || !claim) return <SkeletonRows className="mt-10" rows={6} />

  const submitted = claim.status !== ClaimStatus.DRAFT || justSubmitted
  const today = formatFullDate(new Date())
  // A claim staff moved straight past Submitted has no date, and today's would change daily.
  const submittedOn = claim.submissionDate
    ? formatFullDate(new Date(`${claim.submissionDate}T00:00:00`))
    : justSubmitted && today
  const withBroker = justSubmitted || WITH_BROKER.includes(claim.status)

  return (
    <ClaimStepShell
      step={5}
      title="Review & Submit"
      subtitle="Check everything below before submitting. Your broker will review and submit to the carrier."
      showHeader={showHeader}
    >
      {showHeader && (
        <Banner variant="info" small>
          Fields marked <strong>Supplemental</strong> can be added later — they won&apos;t delay your claim
          being opened.
        </Banner>
      )}

      <ClaimReviewSummary
        claim={claim}
        orgName={org?.name ?? ''}
        policyIds={(org?.currentPolicies ?? []).map((p) => p.vendorId).filter(Boolean)}
      />

      {submitted ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-2 py-8 text-center">
            <div className="bg-primary text-primary-foreground flex size-10 items-center justify-center rounded-full text-lg">
              ✓
            </div>
            <h2 className="text-lg font-semibold text-neutral-900">Claim submitted</h2>
            {withBroker && (
              <p className="text-sm text-neutral-600">
                Your broker will review it and submit to the carrier.
              </p>
            )}
            {submittedOn && <p className="text-sm text-neutral-600">Submitted {submittedOn}.</p>}
          </CardContent>
        </Card>
      ) : (
        <>
          <Card>
            <CardHeader>
              <CardTitle>Certification</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-5">
              <Controller
                name="certifyTrue"
                control={control}
                render={({ field }) => (
                  <label className="flex cursor-pointer items-start gap-3">
                    <Checkbox
                      className="mt-0.5"
                      checked={field.value}
                      onCheckedChange={(c) => field.onChange(c === true)}
                    />
                    <span className="text-sm text-neutral-700">
                      I certify that the information provided in this claim is true, accurate, and complete to
                      the best of my knowledge. I understand that providing false or misleading information
                      may result in denial of coverage and/or legal action.
                    </span>
                  </label>
                )}
              />
              <Controller
                name="authorizeInvestigate"
                control={control}
                render={({ field }) => (
                  <label className="flex cursor-pointer items-start gap-3">
                    <Checkbox
                      className="mt-0.5"
                      checked={field.value}
                      onCheckedChange={(c) => field.onChange(c === true)}
                    />
                    <span className="text-sm text-neutral-700">
                      I authorize my insurance broker and carrier to investigate this claim and contact
                      relevant parties, including law enforcement agencies, medical providers, and other
                      insurers as necessary.
                    </span>
                  </label>
                )}
              />
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="flex flex-col gap-1">
                  <label className="text-sm font-medium text-neutral-700">
                    Digital signature (full legal name) *
                  </label>
                  <Input placeholder="Type your full legal name" {...register('signature')} />
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-sm font-medium text-neutral-700">Date</label>
                  <Input value={today} readOnly disabled className="bg-neutral-50 text-neutral-500" />
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-between">
            <Button variant="outline" onClick={onBack}>
              ← Back
            </Button>
            <Button
              type="button"
              disabled={!canSubmit}
              onClick={handleSubmit(onValid)}
              loading={saving ? 'Submitting…' : undefined}
            >
              Submit claim
            </Button>
          </div>
        </>
      )}
    </ClaimStepShell>
  )
}
